import React, { FC, useState, useMemo, useEffect, useCallback } from "react";
import { ColumnConfig, numberSort, SortDirections, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import {
    JobPerformanceSlowReport,
    JobPerformanceDashboardRequest,
} from "@app/operations/job-performance/jobPerformanceInterfaces";
import { JobPerformanceService } from "@app/operations/job-performance/jobPerformanceService";

type SlowReportRow = {
    [wafflesKeyColumnName]: string;
    avgQueueWait:           string;
    avgRunTime:             string;
    avgTotalEndToEnd:       string;
    job:                    string;
    quarantined:            number;
    quarantinedChip:        React.ReactNode;
    reportType:             string;
    reportsGenerated:       number;
    slowerThanAvg:          number;
    slowerThanAvgFormatted: React.ReactNode;
    worstRunTime:           string;
};

const PAGE_SIZE = 10;

/* oxlint-disable sort-keys */
const columnConfig: ColumnConfig = {
    reportType:             { displayText: 'Report Type',                                              sortOrder: 10, testId: 'slow-reports-report-type' },
    job:                    { displayText: 'Job',                                                      sortOrder: 20, testId: 'slow-reports-job' },
    reportsGenerated:       { alignment: TextAlignment.Right, displayText: 'Reports Generated',        sizeContentToFit: true, sortOrder: 30, testId: 'slow-reports-reports-generated' },
    worstRunTime:           { alignment: TextAlignment.Right, displayText: 'Worst Run Time (Min)',     sizeContentToFit: true, sortOrder: 40, testId: 'slow-reports-worst-run-time' },
    avgQueueWait:           { alignment: TextAlignment.Right, displayText: 'Avg Queue Wait (Min)',      sizeContentToFit: true, sortOrder: 50, testId: 'slow-reports-avg-queue-wait' },
    avgRunTime:             { alignment: TextAlignment.Right, displayText: 'Avg Run Time (Min)',        sizeContentToFit: true, sortOrder: 60, testId: 'slow-reports-avg-run-time' },
    avgTotalEndToEnd:       { alignment: TextAlignment.Right, displayText: 'Avg Total End-to-End (Min)', sizeContentToFit: true, sortOrder: 70, testId: 'slow-reports-avg-total-end-to-end' },
    quarantinedChip:        { alignment: TextAlignment.Right, displayText: 'Quarantined',              sizeContentToFit: true, sortOrder: 80, testId: 'slow-reports-quarantined-chip' },
    slowerThanAvgFormatted: { alignment: TextAlignment.Right, displayText: 'Slower Than Avg (x)',      sizeContentToFit: true, sortOrder: 90, testId: 'slow-reports-multiplier-formatted' },
    quarantined:            { displayText: 'Quarantined', hidden: true,           sizeContentToFit: true,             testId: 'slow-reports-quarantined' },
    slowerThanAvg:          { displayText: 'Slower Than Avg (x)', hidden: true,   sizeContentToFit: true,             testId: 'slow-reports-multiplier' },
};
/* oxlint-enable sort-keys */

function formatMultiplier(v: number): React.ReactNode {
    const cls = v >= 7 ? 'jp-multiplier--high' : v >= 3 ? 'jp-multiplier--mid' : 'jp-multiplier--low';
    return <span className={cls}>{v.toFixed(1)}x</span>;
}

const toRow = (r: JobPerformanceSlowReport): SlowReportRow => ({
    avgQueueWait:           r.avgQueueWaitMinutes.toFixed(1),
    avgRunTime:             r.avgRunTimeMinutes.toFixed(1),
    avgTotalEndToEnd:       r.avgTotalEndToEndMinutes.toFixed(1),
    job:                    r.jobTypeName,
    quarantined:            r.quarantinedCount,
    quarantinedChip:        r.quarantinedCount > 0
                                ? <span className="jp-chip jp-chip--active">{r.quarantinedCount}</span>
                                : <span className="jp-chip jp-chip--muted">0</span>,
    reportsGenerated:       r.reportsGenerated,
    reportType:             r.reportTypeName,
    slowerThanAvg:          r.slowerThanAvgMultiplier,
    slowerThanAvgFormatted: formatMultiplier(r.slowerThanAvgMultiplier),
    [wafflesKeyColumnName]: r.reportTypeId.toString(),
    worstRunTime:           r.worstRunTimeMinutes.toFixed(1),
});

type Props = {
    filters: JobPerformanceDashboardRequest;
};

export const JobPerformanceSlowReportsTable: FC<Props> = ({ filters }) => {
    const service = useMemo(() => new JobPerformanceService(), []);

    const [items, setItems]           = useState<JobPerformanceSlowReport[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setItems([]);
        setTotalCount(0);
        service.getSlowReports({ ...filters, offset: 0, pageSize: PAGE_SIZE })
            .then(result => {
                setItems(result.items);
                setTotalCount(result.totalCount);
            })
            .catch(() => setError('Failed to load slow reports. Please try again.'))
            .finally(() => setLoading(false));
    }, [filters]);

    const tableData = useMemo<SlowReportRow[]>(() => items.map(toRow), [items]);

    const loadMore = useCallback((justChecking: boolean): boolean => {
        if (justChecking) return items.length < totalCount;
        service.getSlowReports({ ...filters, offset: items.length, pageSize: PAGE_SIZE })
            .then(result => {
                setItems(prev => [...prev, ...result.items]);
                setTotalCount(result.totalCount);
            })
            .catch(err => console.error('Failed to load more slow reports', err));
        return items.length + PAGE_SIZE < totalCount;
    }, [items.length, totalCount, filters]);

    const handleSlowReportsSort = (segment: SlowReportRow[] | undefined, column: keyof SlowReportRow, direction: SortDirections): SlowReportRow[] => {
        const data = segment !== undefined ? segment : [...tableData];
        const mult = direction === SortDirections.Asc ? 1 : -1;
        if (column === 'avgQueueWait' || column === 'avgRunTime' || column === 'avgTotalEndToEnd' || column === 'worstRunTime' || column === 'reportsGenerated') {
            return numberSort(data, column, mult);
        }
        if (column === 'slowerThanAvg' || column === 'slowerThanAvgFormatted') {
            return [...data].sort((a, b) => (a.slowerThanAvg - b.slowerThanAvg) * mult);
        }
        if (column === 'quarantinedChip' || column === 'quarantined') {
            return [...data].sort((a, b) => (a.quarantined - b.quarantined) * mult);
        }
        return [...data].sort((a, b) => (String(a[column]) > String(b[column]) ? 1 : -1) * mult);
    };

    if (error) return <p className="jp-error-message">{error}</p>;

    return (
        <Waffles<SlowReportRow>
            columns={columnConfig}
            customSort={handleSlowReportsSort}
            data={tableData}
            loadMore={loadMore}
            loadMoreLabel="See More"
            loading={loading}
            testId="job-performance__slow-reports-table"
        />
    );
};
